import { useI18n } from 'vue-i18n'
import { DateFormat } from '@/enums/DateFormat'
import { format, parseISO, parse } from 'date-fns'
import { enUS, de } from 'date-fns/locale'

export function useFormatDate() {
  const { d, locale } = useI18n()

  const formatDate = (
    date: Date | string | number | null | undefined,
    format: DateFormat,
    emptyPlaceholder = '–'
  ): string => {
    if (date === null || date === undefined || date === '') {
      return emptyPlaceholder
    }
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) {
      return emptyPlaceholder
    }

    return d(dateObj, format)
  }

  /** Time only — DE: HH:mm (24h) | EN: hh:mm AM/PM (12h) */
  const formatTime = (date: Date | string | number | null | undefined, emptyPlaceholder?: string) =>
    formatDate(date, DateFormat.TIME, emptyPlaceholder)

  /** Date only — DE: dd.MM.yyyy | EN: MM/DD/YYYY */
  const formatDateOnly = (
    date: Date | string | number | null | undefined,
    emptyPlaceholder?: string
  ) => formatDate(date, DateFormat.DATE, emptyPlaceholder)

  /** Date + time — DE: dd.MM.yyyy, HH:mm | EN: MM/DD/YYYY, hh:mm AM/PM */
  const formatDateTime = (
    date: Date | string | number | null | undefined,
    emptyPlaceholder?: string
  ) => formatDate(date, DateFormat.DATE_TIME, emptyPlaceholder)

  /** Date + time, short year — DE: dd.MM.yy, HH:mm | EN: MM/DD/YY, hh:mm AM/PM */
  const formatDateTimeShort = (
    date: Date | string | number | null | undefined,
    emptyPlaceholder?: string
  ) => formatDate(date, DateFormat.DATE_TIME_SHORT, emptyPlaceholder)

  /** Date only, short year — DE: dd.MM.yy | EN: MM/DD/YY */
  const formatDateShort = (
    date: Date | string | number | null | undefined,
    emptyPlaceholder?: string
  ) => formatDate(date, DateFormat.DATE_SHORT, emptyPlaceholder)

  /** Date + abbreviated month + time — DE: dd. MMM yyyy, HH:mm | EN: MMM DD, yyyy, hh:mm AM/PM */
  const formatDateTimeWithMonth = (
    date: Date | string | number | null | undefined,
    emptyPlaceholder?: string
  ) => formatDate(date, DateFormat.DATE_TIME_WITH_MONTH, emptyPlaceholder)

  /** Date of birth — uses shared format with UTC timezone */
  const formatDateOfBirth = (
    date: Date | string | number | null | undefined,
    emptyPlaceholder?: string
  ) => formatDate(date, DateFormat.DATE_OF_BIRTH, emptyPlaceholder)

  /** time, date — e.g. DE: "14:30, 23. Apr. 2026" | EN: "02:30 PM, Apr 23, 2026" */
  const formatTimeAndDate = (
    date: Date | string | number | null | undefined,
    emptyPlaceholder = '–'
  ): string => {
    if (date === null || date === undefined || date === '') {
      return emptyPlaceholder
    }
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) {
      return emptyPlaceholder
    }
    const time = d(dateObj, DateFormat.TIME)
    const dateStr = new Intl.DateTimeFormat(locale.value, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(dateObj)
    return `${time}, ${dateStr}`
  }

  const formatConferenceDate = (
    date: Date | string | number | null | undefined,
    emptyPlaceholder = '–'
  ): string => {
    if (date === null || date === undefined || date === '') {
      return emptyPlaceholder
    }
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) {
      return emptyPlaceholder
    }
    if (locale.value === 'de') {
      return format(dateObj, "dd.MM.yy, HH:mm 'Uhr'", { locale: de })
    }
    return format(dateObj, "do MMMM yyyy 'at' HH:mm", { locale: enUS })
  }

  const formatToUTCISO = (dateString: string | undefined | null): string | undefined => {
    if (!dateString) return undefined
    let parsedDate: Date

    if (dateString.includes('T')) {
      parsedDate = parseISO(dateString)
    } else {
      parsedDate = parse(dateString, 'yyyy-MM-dd HH:mm', new Date())
    }

    return parsedDate.toISOString()
  }

  return {
    formatDate,
    formatTime,
    formatDateOnly,
    formatDateTime,
    formatDateTimeShort,
    formatDateShort,
    formatDateTimeWithMonth,
    formatDateOfBirth,
    formatTimeAndDate,
    formatConferenceDate,
    formatToUTCISO
  }
}
